import { PatrickServiceClient } from '@policyfly/protobuf/patrick'
import { isJWTValid } from '@policyfly/utils/api'
import { GrpcStatusCode } from '@protobuf-ts/grpcweb-transport'
import { RpcError, RpcOutputStreamController, ServerStreamingCall, UnaryCall } from '@protobuf-ts/runtime-rpc'

import { useAuthenticationStore } from '@/stores/authentication'

import type { CreateInterceptorParams } from '@/stores/api'
import type { RpcInterceptor, RpcMetadata, RpcOptions } from '@protobuf-ts/runtime-rpc'

/**
 * Returns the promise unchanged, but stops it reporting an unhandled rejection.
 * A streaming call surfaces its errors through its messages, so these are often never awaited,
 * matching how the transport treats its own streaming calls.
 */
function suppressUnhandled<T> (promise: Promise<T>): Promise<T> {
  promise.catch(() => {})
  return promise
}

/**
 * Creates an interceptor that sets metadata on every unary and server streaming request.
 */
function createMetaRpcInterceptor (getMeta: () => RpcMetadata): RpcInterceptor {
  function setMeta (options: RpcOptions): void {
    options.meta = Object.assign(options.meta ?? {}, getMeta())
  }
  return {
    interceptUnary (next, method, input, options): UnaryCall {
      setMeta(options)
      return next(method, input, options)
    },
    interceptServerStreaming (next, method, input, options): ServerStreamingCall {
      setMeta(options)
      return next(method, input, options)
    },
  }
}

/**
 * Returns the token for the `authorization` header of authenticated requests.
 * Will use the {@link CreateInterceptorParams.accessToken accessToken} if valid
 * or try and refresh it using the {@link CreateInterceptorParams.refreshToken refreshToken}.
 */
export async function getAccessToken (params: CreateInterceptorParams): Promise<string> {
  if (isJWTValid(params.accessToken.value)) return params.accessToken.value
  if (!isJWTValid(params.refreshToken.value)) throw new RpcError('Invalid refresh token', GrpcStatusCode[GrpcStatusCode.UNAUTHENTICATED])
  try {
    const patrickServiceClient = new PatrickServiceClient(params.transportAnonymous)
    const { response } = await patrickServiceClient.refreshToken({ refresh: params.refreshToken.value })
    const token = response.access
    params.accessToken.value = token
    return token
  } catch (err) {
    console.error(err)
    throw new RpcError('Failed to get new access token', GrpcStatusCode[GrpcStatusCode.UNAUTHENTICATED])
  }
}

/**
 * Adds the `authorization` header to GRPC requests, using {@link getAccessToken}.
 *
 * See https://github.com/timostamm/protobuf-ts/issues/31#issuecomment-733025632 for how this supports Promises.
 */
export function createAuthRpcInterceptor (params: CreateInterceptorParams): RpcInterceptor {
  async function authorize (options: RpcOptions): Promise<void> {
    const token = await getAccessToken(params)
    if (!options.meta) {
      options.meta = {}
    }
    options.meta.authorization = token
  }
  return {
    interceptUnary (next, method, input, options): UnaryCall {
      const interceptorPromise = authorize(options).then(() => next(method, input, options))
      return new UnaryCall(
        method,
        options.meta ?? {},
        input,
        interceptorPromise.then((p) => p.headers),
        interceptorPromise.then((p) => p.response),
        interceptorPromise.then((p) => p.status),
        interceptorPromise.then((p) => p.trailers),
      )
    },
    interceptServerStreaming (next, method, input, options): ServerStreamingCall {
      const interceptorPromise = authorize(options).then(() => ({ call: next(method, input, options) }))
      const responses = new RpcOutputStreamController<object>()
      interceptorPromise.then(
        ({ call }) => call.responses.onNext((message, error, complete) => {
          if (message) responses.notifyMessage(message)
          if (error) responses.notifyError(error)
          if (complete) responses.notifyComplete()
        }),
        (error: Error) => responses.notifyError(error),
      )
      return new ServerStreamingCall(
        method,
        options.meta ?? {},
        input,
        suppressUnhandled(interceptorPromise.then(({ call }) => call.headers)),
        responses,
        suppressUnhandled(interceptorPromise.then(({ call }) => call.status)),
        suppressUnhandled(interceptorPromise.then(({ call }) => call.trailers)),
      )
    },
  }
}

/**
 * Adds the `slug` header to GRPC requests.
 * This is extracted from the authentication store or defaulted to an empty string.
 */
export function createSlugRpcInterceptor (): RpcInterceptor {
  return createMetaRpcInterceptor(() => {
    const authenticationStore = useAuthenticationStore()
    return { slug: authenticationStore.slug || '' }
  })
}

/**
 * Adds the `django-token` header to GRPC requests.
 * This is extracted from the api store params or defaulted to an empty string.
 */
export function createDjangoTokenRpcInterceptor (params: CreateInterceptorParams): RpcInterceptor {
  return createMetaRpcInterceptor(() => {
    return { 'django-token': params.spaToken.value ?? '' }
  })
}
